/**
 * Bitcoin uses double SHA-256 for transaction IDs.
 * TXID = SHA256(SHA256(serialized_tx)) displayed in reversed byte order.
 *
 * This is isomorphic: uses Web Crypto API which is available in both
 * Node.js (>= 15) and modern browsers.
 */

/**
 * Compute double SHA-256 hash of the input bytes.
 * Returns 32 bytes.
 */
export async function doubleSha256(data: Uint8Array): Promise<Uint8Array> {
  // First SHA-256
  const first = await crypto.subtle.digest('SHA-256', data as ArrayBufferView<ArrayBuffer>);
  // Second SHA-256
  const second = await crypto.subtle.digest('SHA-256', first);
  return new Uint8Array(second);
}

/**
 * Hash a serialized Bitcoin transaction.
 * Note: The returned hash is in INTERNAL byte order.
 * To get the TXID (display order), reverse the bytes.
 */
export async function hashBitcoinTransaction(serialized: Uint8Array): Promise<Uint8Array> {
  return doubleSha256(serialized);
}

/**
 * Reverse a byte array (for converting internal hash to display TXID).
 */
export function reverseBytes(bytes: Uint8Array): Uint8Array {
  const reversed = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    reversed[i] = bytes[bytes.length - 1 - i] ?? 0;
  }
  return reversed;
}

/**
 * Compute the legacy sighash for a specific input.
 *
 * Legacy sighash (SIGHASH_ALL) process:
 * 1. Start with the unsigned transaction
 * 2. Clear ALL scriptSigs to empty
 * 3. Set the scriptSig of the input being signed to the prevScriptPubKey
 * 4. Append sighash type as 4 bytes LE (01000000 for SIGHASH_ALL)
 * 5. Double SHA-256 the result
 *
 * The result is the 32-byte hash that the private key actually signs.
 */
export async function computeLegacySighash(
  txSerialized: Uint8Array,
  inputIndex: number,
  prevScriptPubKey: Uint8Array,
  sighashType: number = 0x01, // SIGHASH_ALL
): Promise<Uint8Array> {
  // We need to rebuild the serialized transaction with:
  // - All scriptSigs cleared to empty
  // - The target input's scriptSig replaced with prevScriptPubKey
  // - Sighash type appended as 4 bytes LE

  // Parse the transaction to modify it
  const reader = new BufferReaderSimple(txSerialized);

  const parts: Uint8Array[] = [];

  // Version (4 bytes)
  parts.push(reader.read(4));

  // Input count (varint)
  const inputCountBytes = reader.readVarIntRaw();
  parts.push(inputCountBytes.bytes);
  const inputCount = inputCountBytes.value;

  // Inputs
  for (let i = 0; i < inputCount; i++) {
    // Previous txid (32 bytes)
    parts.push(reader.read(32));
    // Previous vout (4 bytes)
    parts.push(reader.read(4));

    // Read original scriptSig
    const scriptLenRaw = reader.readVarIntRaw();
    const scriptLen = scriptLenRaw.value;
    reader.read(scriptLen); // consume and discard scriptSig

    if (i === inputIndex) {
      // Replace with prevScriptPubKey
      parts.push(encodeVarInt(prevScriptPubKey.length));
      parts.push(prevScriptPubKey);
    } else {
      // Empty scriptSig
      parts.push(new Uint8Array([0x00]));
    }

    // Sequence (4 bytes)
    parts.push(reader.read(4));
  }

  // Output count + outputs (copy as-is)
  const outputCountBytes = reader.readVarIntRaw();
  parts.push(outputCountBytes.bytes);
  const outputCount = outputCountBytes.value;

  for (let i = 0; i < outputCount; i++) {
    // Value (8 bytes)
    parts.push(reader.read(8));
    // ScriptPubKey (varint + data)
    const scriptLenRaw = reader.readVarIntRaw();
    parts.push(scriptLenRaw.bytes);
    parts.push(reader.read(scriptLenRaw.value));
  }

  // Locktime (4 bytes)
  parts.push(reader.read(4));

  // Append sighash type as 4 bytes LE
  const sighashBytes = new Uint8Array(4);
  new DataView(sighashBytes.buffer).setUint32(0, sighashType, true);
  parts.push(sighashBytes);

  // Concatenate and double SHA-256
  let totalLen = 0;
  for (const p of parts) totalLen += p.length;
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const p of parts) {
    result.set(p, offset);
    offset += p.length;
  }

  return doubleSha256(result);
}

// ── Helpers for sighash computation ────────────────────────────

class BufferReaderSimple {
  private readonly data: Uint8Array;
  private readonly view: DataView;
  private pos: number;

  constructor(data: Uint8Array) {
    this.data = data;
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this.pos = 0;
  }

  read(count: number): Uint8Array {
    const slice = this.data.slice(this.pos, this.pos + count);
    this.pos += count;
    return slice;
  }

  readVarIntRaw(): { value: number; bytes: Uint8Array } {
    const first = this.data[this.pos]!;
    if (first < 0xfd) {
      this.pos += 1;
      return { value: first, bytes: new Uint8Array([first]) };
    }
    if (first === 0xfd) {
      const val = this.view.getUint16(this.pos + 1, true);
      const bytes = this.data.slice(this.pos, this.pos + 3);
      this.pos += 3;
      return { value: val, bytes };
    }
    if (first === 0xfe) {
      const val = this.view.getUint32(this.pos + 1, true);
      const bytes = this.data.slice(this.pos, this.pos + 5);
      this.pos += 5;
      return { value: val, bytes };
    }
    const val = Number(this.view.getBigUint64(this.pos + 1, true));
    const bytes = this.data.slice(this.pos, this.pos + 9);
    this.pos += 9;
    return { value: val, bytes };
  }
}

/**
 * Compute the BIP-143 SegWit sighash for a specific input.
 *
 * BIP-143 sighash (SIGHASH_ALL) is a hash of these fields in order:
 *   1. nVersion (4 bytes LE)
 *   2. hashPrevouts = SHA256d(all outpoints: txid+vout)
 *   3. hashSequence = SHA256d(all sequences)
 *   4. outpoint of the input being signed (txid 32 bytes + vout 4 bytes)
 *   5. scriptCode (for P2WPKH: OP_DUP OP_HASH160 <20-byte-hash> OP_EQUALVERIFY OP_CHECKSIG)
 *   6. value of the UTXO being spent (8 bytes LE)
 *   7. nSequence of the input being signed (4 bytes LE)
 *   8. hashOutputs = SHA256d(all outputs serialized)
 *   9. nLockTime (4 bytes LE)
 *  10. sighash type (4 bytes LE)
 *
 * @param txSerialized - The serialized unsigned transaction (legacy format)
 * @param inputIndex - Which input is being signed
 * @param prevScriptPubKey - The scriptPubKey of the UTXO being spent
 * @param prevValue - The value of the UTXO being spent in satoshis
 * @param sighashType - Sighash type (default: SIGHASH_ALL = 0x01)
 */
export async function computeSegWitSighash(
  txSerialized: Uint8Array,
  inputIndex: number,
  prevScriptPubKey: Uint8Array,
  prevValue: bigint,
  sighashType: number = 0x01,
): Promise<Uint8Array> {
  const reader = new BufferReaderSimple(txSerialized);

  // Parse the transaction to extract components
  const version = reader.read(4); // nVersion

  const inputCountRaw = reader.readVarIntRaw();
  const inputCount = inputCountRaw.value;

  // Collect all input data
  const inputs: { prevHash: Uint8Array; prevIndex: Uint8Array; sequence: Uint8Array }[] = [];
  for (let i = 0; i < inputCount; i++) {
    const prevHash = reader.read(32);
    const prevIndex = reader.read(4);
    const scriptLen = reader.readVarIntRaw().value;
    reader.read(scriptLen); // skip scriptSig
    const sequence = reader.read(4);
    inputs.push({ prevHash, prevIndex, sequence });
  }

  const outputCountRaw = reader.readVarIntRaw();
  const outputCount = outputCountRaw.value;

  // Collect all output data (serialized)
  const outputParts: Uint8Array[] = [];
  for (let i = 0; i < outputCount; i++) {
    const value = reader.read(8);
    const scriptLenRaw = reader.readVarIntRaw();
    const script = reader.read(scriptLenRaw.value);
    outputParts.push(value, scriptLenRaw.bytes, script);
  }

  const locktime = reader.read(4);

  // 2. hashPrevouts = SHA256d(all outpoints)
  const prevoutsData = concatAll(inputs.flatMap((inp) => [inp.prevHash, inp.prevIndex]));
  const hashPrevouts = await doubleSha256(prevoutsData);

  // 3. hashSequence = SHA256d(all sequences)
  const sequenceData = concatAll(inputs.map((inp) => inp.sequence));
  const hashSequence = await doubleSha256(sequenceData);

  // 4. outpoint being signed
  const input = inputs[inputIndex]!;
  const outpoint = concatAll([input.prevHash, input.prevIndex]);

  // 5. scriptCode — for P2WPKH, derive from scriptPubKey
  //    P2WPKH scriptPubKey: 0x0014{20-byte-hash}
  //    scriptCode: 0x1976a914{20-byte-hash}88ac
  let scriptCode: Uint8Array;
  if (prevScriptPubKey.length === 22 && prevScriptPubKey[0] === 0x00 && prevScriptPubKey[1] === 0x14) {
    // P2WPKH: extract 20-byte pubkey hash and wrap in P2PKH script
    const pubkeyHash = prevScriptPubKey.slice(2);
    scriptCode = new Uint8Array([0x19, 0x76, 0xa9, 0x14, ...pubkeyHash, 0x88, 0xac]);
  } else if (prevScriptPubKey.length === 34 && prevScriptPubKey[0] === 0x00 && prevScriptPubKey[1] === 0x20) {
    // P2WSH: scriptCode is the witnessScript (not available here, use scriptPubKey as fallback)
    const len = prevScriptPubKey.length;
    scriptCode = new Uint8Array([len, ...prevScriptPubKey]);
  } else {
    // Fallback: use the scriptPubKey with length prefix
    const len = prevScriptPubKey.length;
    scriptCode = new Uint8Array([len, ...prevScriptPubKey]);
  }

  // 6. value (8 bytes LE)
  const valueBytes = new Uint8Array(8);
  new DataView(valueBytes.buffer).setBigUint64(0, prevValue, true);

  // 7. nSequence of the input being signed
  const nSequence = input.sequence;

  // 8. hashOutputs = SHA256d(all outputs serialized)
  const outputsData = concatAll(outputParts);
  const hashOutputs = await doubleSha256(outputsData);

  // 10. sighash type (4 bytes LE)
  const sighashBytes = new Uint8Array(4);
  new DataView(sighashBytes.buffer).setUint32(0, sighashType, true);

  // Concatenate the preimage
  const preimage = concatAll([
    version,         // 1. nVersion
    hashPrevouts,    // 2. hashPrevouts
    hashSequence,    // 3. hashSequence
    outpoint,        // 4. outpoint
    scriptCode,      // 5. scriptCode
    valueBytes,      // 6. amount
    nSequence,       // 7. nSequence
    hashOutputs,     // 8. hashOutputs
    locktime,        // 9. nLockTime
    sighashBytes,    // 10. nHashType
  ]);

  return doubleSha256(preimage);
}

function concatAll(arrays: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const a of arrays) len += a.length;
  const result = new Uint8Array(len);
  let offset = 0;
  for (const a of arrays) {
    result.set(a, offset);
    offset += a.length;
  }
  return result;
}

function encodeVarInt(value: number): Uint8Array {
  if (value < 0xfd) return new Uint8Array([value]);
  if (value <= 0xffff) {
    const buf = new Uint8Array(3);
    buf[0] = 0xfd;
    new DataView(buf.buffer).setUint16(1, value, true);
    return buf;
  }
  const buf = new Uint8Array(5);
  buf[0] = 0xfe;
  new DataView(buf.buffer).setUint32(1, value, true);
  return buf;
}
